import { IMG } from "@/lib/site";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import FAQ from "@/components/site/FAQ";
import VideoTrio from "@/components/site/VideoTrio";
import { Check } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { useJsonLd, buildMedicalConditionSchema } from "@/lib/useJsonLd";
import { useFaqs } from "@/lib/faqs";
import { useT } from "@/lib/i18n";

const Oncologia = () => {
  const t = useT();
  const { oncology } = useFaqs();
  useSeo({
    title: {
      es: "Tengo cáncer ¿ahora qué? · Acompañamiento Oncológico Online | Método TAO",
      ca: "Tinc càncer i ara què? · Acompanyament Oncològic Online | Mètode TAO",
    },
    description: {
      es: "Si tienes un diagnóstico de cáncer y no sabes por dónde empezar: te acompaño durante quimio, radio, cirugía, hormonoterapia y secuelas. Yoga oncológico, respiración y regulación del sistema nervioso. Online (España y Latinoamérica) y presencial en Barcelona.",
      ca: "Si tens un diagnòstic de càncer i no saps per on començar: t'acompanyo durant quimio, radio, cirurgia, hormonoteràpia i seqüeles. Ioga oncològic, respiració i regulació del sistema nerviós. Online (Espanya i Llatinoamèrica) i presencial a Barcelona.",
    },
    path: "/oncologia",
  });
  // FAQ schema is injected by the <FAQ> component itself — do NOT duplicate here.
  useJsonLd("condition-oncologia", buildMedicalConditionSchema({
    name: "Cáncer",
    alternateName: ["Oncología", "Cáncer de mama", "Superviviente de cáncer"],
    description: "Acompañamiento cuerpo-mente durante y después de un proceso oncológico: quimio, radio, cirugía, hormonoterapia y secuelas.",
    url: "https://metodo-tao.com/oncologia",
  }));

  const phases = [
    t("onco.phase.1"),
    t("onco.phase.2"),
    t("onco.phase.3"),
    t("onco.phase.4"),
    t("onco.phase.5"),
    t("onco.phase.6"),
    t("onco.phase.7"),
    t("onco.phase.8"),
  ];

  const helps = [
    t("onco.help.1"),
    t("onco.help.2"),
    t("onco.help.3"),
    t("onco.help.4"),
    t("onco.help.5"),
  ];

  return (
  <div className="bg-ivory">
    <section className="pt-12 md:pt-20 pb-16 md:pb-24" data-testid="page-oncologia">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <Reveal className="lg:col-span-7">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">{t("onco.eyebrow")}</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal leading-[0.98]">
            {t("onco.title1")} <span className="italic text-terracotta">{t("onco.title2")}</span>.
          </h1>
          <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-xl">
            {t("onco.lead")}
          </p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="aspect-[4/5] rounded-tr-[4rem] rounded-bl-[4rem] overflow-hidden bg-sand">
            <img src={IMG.oncologyMeditation} alt="Yoga oncológico - paciente practicando equilibrio con bloque · Método TAO" className="w-full h-full object-cover" fetchPriority="high" decoding="async" />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-20 md:py-28 bg-sand/30 grain">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16">
        <Reveal className="md:col-span-5">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">{t("onco.forWho")}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
            {t("onco.phasesTitle1")} <span className="italic">{t("onco.phasesTitle2")}</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-7">
          <ul className="grid sm:grid-cols-2 gap-x-6">
            {phases.map((p) => (
              <li key={p} className="flex items-center gap-3 py-4 border-b border-charcoal/10 text-charcoal/85">
                <span className="w-2 h-2 rounded-full bg-terracotta" />
                <span className="text-[15px]">{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>

    <VideoTrio />

    <section className="py-20 md:py-28">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
        <Reveal className="md:col-span-5">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">{t("onco.helpEyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
            {t("onco.helpTitle1")} <span className="italic">{t("onco.helpTitle2")}</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-7">
          <ul className="space-y-1">
            {helps.map((h, i) => (
              <li key={h} className="flex items-center gap-4 py-5 border-b border-sand/70" data-testid={`onco-benefit-${i}`}>
                <span className="w-9 h-9 rounded-full border border-terracotta/50 flex items-center justify-center text-terracotta">
                  <Check size={14} />
                </span>
                <span className="text-[16px] text-charcoal/85">{h}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>

    <FAQ
      eyebrow={t("onco.faqEyebrow")}
      title={t("onco.faqTitle")}
      items={oncology}
    />

    <SectionCTA title={t("onco.ctaTitle")} body={t("onco.ctaBody")} />
  </div>
  );
};

export default Oncologia;
